// mergeOpenApi.js (ESM)
// Merge multiple OpenAPI (3.0/3.1) specs into ONE spec for a single Redoc page.
// - Prefixes components to avoid collisions
// - Converts */* -> application/json
// - Ensures every operation has at least one tag
// - Merges tags and x-tagGroups so the sidebar shows collapsible groups

const METHODS = ["get", "post", "put", "delete", "patch", "head", "options", "trace"];

const COMPONENT_KINDS = ["schemas", "parameters", "responses", "requestBodies", "headers", "securitySchemes", "links", "callbacks"];

const isOp = (k) => METHODS.includes(k);
const slug = (s) =>
    String(s || "")
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-_]/g, "");

function walk(obj, fn) {
    if (Array.isArray(obj)) {
        obj.forEach((v) => walk(v, fn));
        return;
    }
    if (obj && typeof obj === "object") {
        fn(obj);
        Object.values(obj).forEach((v) => walk(v, fn));
    }
}

function replaceRefs(spec, refMap) {
    walk(spec, (n) => {
        if (n.$ref && refMap[n.$ref]) n.$ref = refMap[n.$ref];
    });
}

function prefixComponents(spec, prefix) {
    if (!spec.components) return {};
    const refMap = {};
    for (const kind of COMPONENT_KINDS) {
        const bucket = spec.components[kind];
        if (!bucket) continue;
        for (const [oldName, val] of Object.entries({ ...bucket })) {
            const newName = `${prefix}_${oldName}`;
            const oldRef = `#/components/${kind}/${oldName}`;
            const newRef = `#/components/${kind}/${newName}`;
            bucket[newName] = val;
            delete bucket[oldName];
            refMap[oldRef] = newRef;
        }
    }
    replaceRefs(spec, refMap);
    return refMap;
}

function ensureAppJsonResponses(spec) {
    for (const [, item] of Object.entries(spec.paths || {})) {
        for (const [m, op] of Object.entries(item || {})) {
            if (!isOp(m)) continue;
            const responses = op.responses || {};
            for (const [, r] of Object.entries(responses)) {
                if (!r || !r.content) continue;
                if (r.content["*/*"] && !r.content["application/json"]) {
                    r.content["application/json"] = r.content["*/*"];
                    delete r.content["*/*"];
                }
            }
        }
    }
}

function injectFallbackTag(spec, tagName) {
    for (const [, item] of Object.entries(spec.paths || {})) {
        for (const [m, op] of Object.entries(item || {})) {
            if (!isOp(m)) continue;
            if (!Array.isArray(op.tags) || op.tags.length === 0) op.tags = [tagName];
        }
    }
}

function mergeTagArrays(...tagLists) {
    const map = new Map();
    for (const list of tagLists) {
        (list || []).forEach((t) => {
            if (!t?.name) return;
            if (!map.has(t.name)) map.set(t.name, { name: t.name, description: t.description, externalDocs: t.externalDocs });
            // keep first non-empty description/externalDocs
            const cur = map.get(t.name);
            if (!cur.description && t.description) cur.description = t.description;
            if (!cur.externalDocs && t.externalDocs) cur.externalDocs = t.externalDocs;
        });
    }
    return [...map.values()];
}

function mergeTagGroups(existingGroups, groupDefs) {
    // Dedup by (name -> set(tags))
    const result = [];
    const key = (g) => `${g.name}::${(g.tags || []).join("|")}`;
    const seen = new Set();
    [...(existingGroups || []), ...(groupDefs || [])].forEach((g) => {
        if (!g?.name || !Array.isArray(g.tags)) return;
        const k = key(g);
        if (!seen.has(k)) {
            seen.add(k);
            result.push({ name: g.name, tags: [...new Set(g.tags)] });
        }
    });
    return result;
}

export async function loadAndMerge(urls, labels = [], options = {}) {
    const specs = await Promise.all(
        urls.map(async (u) => {
            const r = await fetch(u);
            if (!r.ok) throw new Error(`Failed to fetch ${u}: ${r.status}`);
            return r.json();
        })
    );
    return mergeOpenApi(specs, labels, options); // pass options down
}

export function mergeOpenApi(specs, labels = [], options = {}) {
    if (!Array.isArray(specs) || specs.length === 0) throw new Error("No specs supplied");

    // take title/version from first spec if present
    const firstInfo = specs.find((s) => s.info)?.info || { title: "Unified API", version: "1.0.0" };

    const merged = {
        openapi: specs.find((s) => s.openapi)?.openapi || "3.0.3",
        info: { ...firstInfo },
        servers: [],
        tags: [],
        paths: {},
        components: {
            schemas: {},
            parameters: {},
            responses: {},
            requestBodies: {},
            headers: {},
            securitySchemes: {},
            links: {},
            callbacks: {},
        },
        "x-tagGroups": [],
    };

    // Collect tags to group under Documentation vs Services
    const docTagCandidates = new Set(["Integration Guide","guides", "widget", "guide", "docs", "documentation"]);
    const serviceTags = new Set();
    const docTags = new Set();

    const serverSeen = new Set();

    specs.forEach((raw, i) => {
        if (!raw || typeof raw !== "object") throw new Error(`Spec[${i}] is not an object`);
        const spec = JSON.parse(JSON.stringify(raw));
        const label = labels[i] || spec.info?.title || `API ${i + 1}`;
        const prefix = slug(label).replace(/-/g, "_") || `api${i + 1}`;

        // Normalize for Redoc
        ensureAppJsonResponses(spec);
        injectFallbackTag(spec, label);
        prefixComponents(spec, prefix);

        // servers
        (spec.servers || []).forEach((s) => {
            const k = s.url;
            if (!serverSeen.has(k)) {
                serverSeen.add(k);
                merged.servers.push(s);
            }
        });

        // paths (resolve path-level conflicts by namespacing conflicting methods under /<label>/<path>)
        for (const [p, item] of Object.entries(spec.paths || {})) {
            if (!merged.paths[p]) {
                merged.paths[p] = item;
                continue;
            }
            const target = { ...merged.paths[p] };
            const src = item;
            const ns = `/${slug(label)}${p.startsWith("/") ? "" : "/"}${p}`;
            for (const [m, op] of Object.entries(src)) {
                if (!isOp(m) || !target[m]) {
                    target[m] = op;
                    continue;
                }
                merged.paths[ns] = merged.paths[ns] || {};
                merged.paths[ns][m] = op;
            }
            merged.paths[p] = target;
        }

        // components (safe after prefixing)
        for (const kind of COMPONENT_KINDS) {
            Object.assign(merged.components[kind], spec.components?.[kind]);
        }

        // tags and grouping classification
        (spec.tags || []).forEach((t) => {
            if (!t?.name) return;
            const name = t.name;
            const key = slug(name);
            if (docTagCandidates.has(key) || /guide|readme|doc/i.test(name)) docTags.add(name);
            else serviceTags.add(name);
        });

        // Merge tag objects
        merged.tags = mergeTagArrays(merged.tags, spec.tags);

        const { logoUrl = "/logo.svg", logoAltText = "Logo", logoBg = "transparent", logoHref, overrideLogo = true } = options;

        const logoObj = { url: logoUrl, altText: logoAltText, backgroundColor: logoBg };
        if (logoHref) logoObj.href = logoHref;

        // only set if user wants to, or if there isn't one already
        if (overrideLogo || !merged.info["x-logo"]) {
            merged.info["x-logo"] = logoObj;
        }

        return merged;
    });

    // tagGroups: keep any existing groups from inputs, then add our defaults
    const inputGroups = specs.flatMap((s) => s["x-tagGroups"] || []);
    const defaultGroups = [];
    if (docTags.size) defaultGroups.push({ name: "Documentation", tags: [...docTags] });
    if (serviceTags.size) defaultGroups.push({ name: "Services", tags: [...serviceTags] });
    merged["x-tagGroups"] = mergeTagGroups(inputGroups, defaultGroups);

    return merged;
}
